import React, { FC } from 'react';
import styled from '@emotion/styled';
import { colors } from '@modernhealth/vital-foundation';

import {
    BodyTextSmall,
    HeadingText30,
    HeadingText60,
    Icon,
    SkeletonPlaceholder,
    pxToRem,
} from '@modernhealth/vital-components';

import { useHistory } from 'react-router';
import { TrackClick } from '@modernhealth/web-analytics';

import { CareRecommendationBadge } from '../components/CareRecommendedBadge';
import { useExploreCareOptionsScreenQuery } from '../documents/exploreCareOptionsScreen.generated';
import {
    ContentCollectionPlatform,
    ExternalLinkNode,
    InternalLinkNode,
} from 'src/generated/webapp_gql';
import { navigationService } from 'src/config/rootService';
import { Container, IconContainer } from '../components/Common';
import { useTranslation } from 'react-i18next';

interface CareOptionProps {
    eventName: string; // used as a key and for analytics
    title: string;
    subtitle?: string;
    image: string;
    onClick: () => void;
    recommended?: boolean | null;
    index?: number;
}

const CareOption: FC<CareOptionProps> = ({
    eventName,
    title,
    subtitle,
    image,
    onClick,
    recommended = false,
    index,
}) => {
    return (
        <TrackClick
            eventName={eventName}
            eventProperties={{
                recommended_badge: recommended,
                element_index: index,
            }}
        >
            <CareOptionContainer onClick={onClick} tabIndex={0}>
                {recommended && <CareRecommendationBadge />}
                <CareOptionLeftColumn>
                    <HeadingText30 marginTop={4}>{title}</HeadingText30>
                    <BodyTextSmall marginTop={8}>{subtitle}</BodyTextSmall>
                </CareOptionLeftColumn>
                <img src={image} alt="" />
            </CareOptionContainer>
        </TrackClick>
    );
};

export const CareOptions = () => {
    const history = useHistory();
    const { t } = useTranslation();
    const { data, loading } = useExploreCareOptionsScreenQuery({
        variables: {
            platform: ContentCollectionPlatform.Web,
        },
        fetchPolicy: 'network-only',
    });

    if (loading) {
        return (
            <Container>
                <SkeletonPlaceholder width={300} height={40} marginTop={40} />
                <SkeletonPlaceholder width={500} height={20} marginTop={14} marginBottom={84} />
                <ActionsContainer>
                    {[{}, {}, {}].map((_, index) => (
                        <ProviderActionItem key={`placeholder_${index}`}>
                            <ItemContent>
                                <SkeletonPlaceholder width={100} height={20} />
                                <SkeletonPlaceholder width={300} height={18} marginTop={4} />
                            </ItemContent>
                            <SkeletonPlaceholder width={60} height={60} borderRadius={30} />
                        </ProviderActionItem>
                    ))}
                </ActionsContainer>
            </Container>
        );
    }

    const cards = (
        data?.exploreCareOptionsScreen?.screenSections?.[0].contentCollectionLinks ?? []
    ).filter((card): card is InternalLinkNode | ExternalLinkNode => {
        if (!card.__typename) {
            return false;
        }
        return ['InternalLinkNode', 'ExternalLinkNode'].includes(card.__typename);
    });

    if (!cards || cards?.length === 0) {
        return null;
    }

    return (
        <Container>
            <IconContainer onClick={() => history.goBack()}>
                <Icon name="arrow-left" />
            </IconContainer>
            <HeadingText60 centered css={{ marginBottom: pxToRem(13) }}>
                {t('care:care_options.title')}
            </HeadingText60>
            <BodyTextSmall centered>{t('care:care_options.description')}</BodyTextSmall>
            <Content>
                {cards.map((careOption: InternalLinkNode | ExternalLinkNode) => (
                    <CareOption
                        key={careOption.uuid}
                        eventName={careOption.titleTranslationKey}
                        title={careOption.title}
                        subtitle={careOption.subtitle ?? ''}
                        image={careOption.image ?? ''}
                        onClick={() => navigationService.navigate(careOption.webValue ?? '')}
                        recommended={
                            careOption.__typename === 'InternalLinkNode'
                                ? careOption.recommended
                                : null
                        }
                    />
                ))}
            </Content>
        </Container>
    );
};

const Content = styled.div({
    display: 'flex',
    flexDirection: 'column',
    maxWidth: pxToRem(670),
    marginTop: pxToRem(72),
    gap: pxToRem(24),
});

const CareOptionContainer = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: pxToRem(32),
    border: `${pxToRem(2)} solid ${colors.gray05}`,
    gap: pxToRem(24),
    position: 'relative',
    borderRadius: pxToRem(6),
    cursor: 'pointer',

    '&:focus-visible': {
        boxShadow: `0px 0px 0px 2px ${colors.cyan20}`,
        outline: 'none',
    },
});

const CareOptionLeftColumn = styled.div({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
});

const ActionsContainer = styled.div({
    width: '100%',
    maxWidth: pxToRem(550),
});

const ProviderActionItem = styled.div`
    margin: 0;
    padding-top: ${pxToRem(39)};
    padding-bottom: ${pxToRem(39)};
    color: ${colors.gray65};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    :not(first-of-type) {
        border-top: ${pxToRem(2)} solid ${colors.gray05};
    }
`;

const ItemContent = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${pxToRem(16)};
`;
